import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { logger } from '@/ui'
import { Button } from '@/ui/rapid-ui'
import { useWhatsAppAccountUpdateFirstContactAutomation, type WhatsAppTemplateData } from '@jobilla/nexus-sdk'
import { Loader2Icon, ZapIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

export function FirstContactAutomationPanel({
	organisationId,
	templates,
	enabled,
	templateId,
	scoreThreshold,
	onSaved,
}: {
	organisationId: string
	templates: WhatsAppTemplateData[]
	enabled: boolean
	templateId: string | null
	scoreThreshold: number | null
	onSaved: () => void
}) {
	const { t } = useTranslation('common')
	const [draftEnabled, setDraftEnabled] = useState(enabled)
	const [draftTemplateId, setDraftTemplateId] = useState<string | null>(templateId)
	const [draftThreshold, setDraftThreshold] = useState<string>(scoreThreshold?.toString() ?? '')
	const [error, setError] = useState('')

	useEffect(() => {
		setDraftEnabled(enabled)
		setDraftTemplateId(templateId)
		setDraftThreshold(scoreThreshold?.toString() ?? '')
	}, [enabled, templateId, scoreThreshold])

	// First-contact automation requires EVERY {{N}} placeholder (1..parameter_count)
	// to have a non-empty mapping. Keys in parameter_map are positional strings
	// ("1", "2", ...) — see WhatsAppTemplateController validation + autofill editor.
	const isFullyAutofilled = (template: WhatsAppTemplateData): boolean => {
		// Generic templates aren't mappable — their canonical autofill always
		// covers every placeholder, so they're always eligible.
		if (template.isGeneric) {
			return true
		}
		if (template.parameterCount === 0) {
			return true
		}
		const map = template.parameterMap
		if (!map) {
			return false
		}
		for (let position = 1; position <= template.parameterCount; position++) {
			const value = map[String(position)]
			if (typeof value !== 'string' || value.trim() === '') {
				return false
			}
		}
		return true
	}

	const approvedTemplates = templates.filter((template) => template.status === 'APPROVED')
	const eligibleTemplates = approvedTemplates.filter(isFullyAutofilled)
	const hasIneligibleApproved = approvedTemplates.length > eligibleTemplates.length

	// If the chosen template falls out of eligibility (e.g. recruiter cleared
	// an autofill slot in another tab), drop it so the user must reselect
	// instead of submitting a now-hidden id.
	useEffect(() => {
		if (draftTemplateId && !eligibleTemplates.some((template) => template.id === draftTemplateId)) {
			setDraftTemplateId(null)
		}
	}, [draftTemplateId, eligibleTemplates])

	const mutation = useWhatsAppAccountUpdateFirstContactAutomation({
		onSuccess: (_data, variables) => {
			setError('')
			logger.track('WhatsApp: First-Contact Automation Toggled', {
				category: 'whatsapp',
				data: {
					organisation_id: variables.organisationId,
					enabled: variables.data.enabled,
					has_template: Boolean(variables.data.templateId),
				},
			})
			onSaved()
		},
		onError: (err) => {
			logger.error(err, {
				tags: { component: 'FirstContactAutomationPanel', action: 'updateFirstContactAutomation' },
				data: { organisationId, responseMessage: err?.response?.data?.message },
			})
			setError(err?.response?.data?.message || t('whatsappSettings.firstContactSaveError'))
		},
	})

	const handleSave = () => {
		if (draftEnabled && !draftTemplateId) {
			setError(t('whatsappSettings.firstContactPickTemplate'))
			return
		}

		if (
			draftEnabled
			&& draftTemplateId
			&& !eligibleTemplates.some((template) => template.id === draftTemplateId)
		) {
			setError(t('whatsappSettings.firstContactPickTemplate'))
			return
		}

		const trimmedThreshold = draftThreshold.trim()
		let scoreThreshold: number | null = null
		if (trimmedThreshold !== '') {
			const parsed = Number(trimmedThreshold)
			if (!Number.isInteger(parsed) || parsed < 66 || parsed > 100) {
				setError(t('whatsappSettings.firstContactThresholdRange'))
				return
			}
			scoreThreshold = parsed
		}

		mutation.mutate({
			organisationId,
			data: {
				enabled: draftEnabled,
				templateId: draftEnabled ? draftTemplateId : null,
				scoreThreshold: scoreThreshold,
			},
		})
	}

	const isDirty = draftEnabled !== enabled
		|| draftTemplateId !== templateId
		|| draftThreshold !== (scoreThreshold?.toString() ?? '')

	return (
		<div className="mt-8 border border-gray-200 rounded-xl p-5">
			<div className="flex items-start gap-3">
				<div className="p-2 rounded-lg bg-amber-50 text-amber-600">
					<ZapIcon className="size-5" />
				</div>
				<div className="flex-1">
					<h3 className="text-lg font-medium text-gray-900">
						{t('whatsappSettings.firstContactAutomationTitle')}
					</h3>
					<p className="text-sm text-gray-500 mt-1">
						{t('whatsappSettings.firstContactAutomationDescription')}
					</p>

					<div className="mt-4 space-y-3">
						<label className="flex items-center gap-2 text-sm">
							<input
								type="checkbox"
								checked={draftEnabled}
								onChange={(event) => setDraftEnabled(event.target.checked)}
								className="size-4 rounded border-gray-300"
								disabled={mutation.isPending}
							/>
							<span>{t('whatsappSettings.firstContactEnableLabel')}</span>
						</label>

						{draftEnabled && (
							<div>
								<label className="block text-sm text-gray-700 mb-1">
									{t('whatsappSettings.firstContactTemplateLabel')}
								</label>
								<Select
									value={draftTemplateId ?? undefined}
									onValueChange={(value) => setDraftTemplateId(value || null)}
									disabled={mutation.isPending}
								>
									<SelectTrigger className="w-full">
										<SelectValue
											placeholder={t('whatsappSettings.firstContactTemplatePlaceholder')}
										/>
									</SelectTrigger>
									<SelectContent>
										{eligibleTemplates.map((template) => (
											<SelectItem key={template.id} value={template.id}>
												{template.name} ({template.language})
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<p className="text-xs text-gray-500 mt-1">
									{t('whatsappSettings.firstContactAutofillRequirement')}
								</p>
								{eligibleTemplates.length === 0 && approvedTemplates.length === 0 && (
									<p className="text-xs text-amber-600 mt-1">
										{t('whatsappSettings.firstContactNoApprovedTemplates')}
									</p>
								)}
								{hasIneligibleApproved && (
									<p className="text-xs text-amber-600 mt-1">
										{t('whatsappSettings.firstContactSomeNotEligible')}
									</p>
								)}
							</div>
						)}
					</div>

					<div className="mt-3">
						<label
							htmlFor="first-contact-threshold"
							className="block text-sm text-gray-700 mb-1"
						>
							{t('whatsappSettings.firstContactThresholdLabel')}
						</label>
						<input
							id="first-contact-threshold"
							aria-label={t('whatsappSettings.firstContactThresholdLabel')}
							type="number"
							min={66}
							max={100}
							value={draftThreshold}
							placeholder="89"
							onChange={(event) => setDraftThreshold(event.target.value)}
							disabled={mutation.isPending}
							className="w-24 rounded border border-gray-300 px-2 py-1 text-sm"
						/>
						<p className="text-xs text-gray-500 mt-1">
							{t('whatsappSettings.firstContactThresholdHelp')}
						</p>
					</div>

					{error && <p className="text-sm text-red-600 mt-3">{error}</p>}

					<div className="mt-4 flex justify-end">
						<Button
							type="neutral"
							onClick={handleSave}
							disabled={!isDirty || mutation.isPending}
						>
							{mutation.isPending
								? (
									<span className="flex items-center gap-2">
										<Loader2Icon className="size-4 animate-spin" />
										{t('whatsappSettings.firstContactSaving')}
									</span>
								)
								: t('whatsappSettings.firstContactSave')}
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}
